/**
 * Stub réutilisable pour les routes admin scaffoldées mais pas encore
 * implémentées. Affiche le titre/description de la route, son ICP, son
 * statut de livraison, et le contrat data binding (table cible, champs lus,
 * champs édités, relations).
 *
 * L'idée : naviguer la nouvelle IA sans tomber sur des 404, et garder le
 * contrat de chaque route visible côté Gaetan pour valider incrémentalement.
 */

export type StubStatus = 'PR 1 stub' | 'PR 1.x en cours' | 'Livrée';

export interface StubPageProps {
  title: string;
  description: string;
  icp: string;
  status: StubStatus;
  tableTarget?: string;
  reads?: string[];
  edits?: string[];
  relations?: string[];
}

const STATUS_CLASSES: Record<StubStatus, string> = {
  'PR 1 stub': 'bg-amber-soft text-amber border-amber/30',
  'PR 1.x en cours': 'bg-founders-soft text-founders-deep border-founders/30',
  Livrée: 'bg-funds-soft text-funds-deep border-funds/30',
};

export function StubPage(props: StubPageProps) {
  return (
    <div className="px-4 md:px-8 py-6 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
              {props.title}
            </h1>
            <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">{props.description}</p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${STATUS_CLASSES[props.status]}`}
          >
            {props.status}
          </span>
        </div>
      </div>

      {/* What this page does */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="md:col-span-2 rounded-md border border-line-soft bg-cream p-5 shadow-card">
          <h2 className="text-sm font-medium text-ink">À quoi sert cette page</h2>
          <p className="mt-2 text-[13px] text-ink">
            ICP : <span className="font-medium">{props.icp}</span>
          </p>
          <p className="mt-3 text-xs text-ink-muted">
            Le contrat data binding est défini dans le brief de refonte admin (section 10).
            L&apos;implémentation viendra dans une PR dédiée selon le séquencement validé.
          </p>
        </div>
        <div className="rounded-md border border-dashed border-line bg-cream-tint p-5 text-xs text-ink-muted">
          <p className="text-[11px] uppercase tracking-wider text-ink-faint font-medium mb-2">
            Mode dégradé Phase 1
          </p>
          <p>
            Cette route est un stub. Tant qu&apos;elle n&apos;est pas implémentée,
            l&apos;édition Airtable se fait dans l&apos;UI Airtable nu. Le count en sidebar est
            branché dès qu&apos;une table existe.
          </p>
        </div>
      </div>

      {/* Data binding contract */}
      {(props.tableTarget || props.reads || props.edits || props.relations) && (
        <div className="rounded-md border border-line-soft bg-cream p-5 shadow-card">
          <h2 className="text-sm font-medium text-ink mb-3">Contrat data binding</h2>
          <dl className="text-xs space-y-2">
            {props.tableTarget && (
              <div className="flex gap-3">
                <dt className="w-32 text-ink-faint uppercase tracking-wider text-[10px] pt-0.5">
                  Table cible
                </dt>
                <dd className="text-ink font-mono">{props.tableTarget}</dd>
              </div>
            )}
            {props.reads && (
              <div className="flex gap-3">
                <dt className="w-32 text-ink-faint uppercase tracking-wider text-[10px] pt-0.5">
                  Lecture
                </dt>
                <dd className="text-ink-muted">{props.reads.join(', ')}</dd>
              </div>
            )}
            {props.edits && (
              <div className="flex gap-3">
                <dt className="w-32 text-ink-faint uppercase tracking-wider text-[10px] pt-0.5">
                  Édition
                </dt>
                <dd className="text-ink-muted">{props.edits.join(', ')}</dd>
              </div>
            )}
            {props.relations && (
              <div className="flex gap-3">
                <dt className="w-32 text-ink-faint uppercase tracking-wider text-[10px] pt-0.5">
                  Relations
                </dt>
                <dd className="text-ink-muted">{props.relations.join(', ')}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
